import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getMethod, postMethod } from "../../services/ApiCallMethods";

export const getReportsList = createAsyncThunk(
  "reports/getReportsList",
  async (
    { currentPage, itemsPerPage, financialYear, quarter, search },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage || 1);
      params.append("limit", itemsPerPage || 20);
      if (search) params.append("search", search);
      if (financialYear) params.append("financial_year", financialYear);
      if (quarter) params.append("quarter", quarter);

      const response = await getMethod(`/api/reports?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch reports");
    }
  }
);

export const syncReports = createAsyncThunk(
  "reports/syncReports",
  async (_, { rejectWithValue }) => {
    try {
      const response = await postMethod(`/api/reports/sync`, {});
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ status: "FAILURE", message: error?.message || "Failed to sync reports" });
    }
  }
);

// Sync now runs in the background (can take hours for a large document set) — this polls
// the singleton report_sync_status row so the UI can tell when it's actually finished.
export const getSyncStatus = createAsyncThunk(
  "reports/getSyncStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/reports/sync-status`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue({ message: error?.message || "Failed to check sync status" });
    }
  }
);

export const getReportFinancialYears = createAsyncThunk(
  "reports/getReportFinancialYears",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMethod(`/api/reports/financial-years`);
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return data.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to fetch financial years");
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: [],
  pagination: {},
  financialYears: [],
  latestRequestId: null,
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReportsList.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        // The unfiltered/all-rows query is much slower than a filtered one — without this,
        // an earlier, slower in-flight request can resolve after a newer, faster one and
        // silently overwrite it. Only the response matching the most recently dispatched
        // request is applied.
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(getReportsList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.loading = false;
        state.data = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getReportsList.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) return;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getReportFinancialYears.fulfilled, (state, action) => {
        state.financialYears = action.payload || [];
      });
  },
});

export default reportsSlice.reducer;
